import React from 'react';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { CertificateDetail as CertificateDetailType } from '@/types';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';

interface Props {
    certificate: CertificateDetailType;
}

const CertificateDetailContent: React.FC<Props> = ({ certificate }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const shareUrl = React.useMemo(() => {
        if (typeof window === 'undefined') {
            return certificate.share_url;
        }

        try {
            return new URL(certificate.share_url, window.location.origin).toString();
        } catch (error) {
            return certificate.share_url;
        }
    }, [certificate.share_url]);

    const downloadUrl = React.useMemo(() => {
        if (typeof window === 'undefined') {
            return certificate.download_url;
        }

        try {
            return new URL(certificate.download_url, window.location.origin).toString();
        } catch (error) {
            return certificate.download_url;
        }
    }, [certificate.download_url]);

    const handleBack = () => {
        router.visit('/my-account/?tab=certificates', {
            preserveState: true,
            preserveScroll: true,
        });
    };

    return (
        <AppLayout>
            <PageTitle title="Certificado" />
            <div className="min-h-screen py-6 sm:py-10">
                <div className="mx-auto max-w-5xl space-y-6 px-4 sm:px-0">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition hover:text-gray-900"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="h-4 w-4"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                            />
                        </svg>
                        {t('certificateDetail.backToCertificates')}
                    </button>

                    <GlassCard className="overflow-hidden rounded-3xl shadow-lg">
                        <div className="flex items-center justify-end gap-3 border-b border-white/40 px-6 py-5 dark:border-white/10 sm:px-8">
                            <a
                                href={downloadUrl}
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-4 w-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12l4.5 4.5m0 0L16.5 12m-4.5 4.5V3"
                                    />
                                </svg>
                                {t('certificateDetail.downloadButton')}
                            </a>
                            <a
                                href={shareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`btn-gradient inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 ${colors.buttonPrimaryFocus}`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-4 w-4"
                                >
                                    <path d="M16.5 6a2.25 2.25 0 11.4 4.472l-8.154 4.077a2.25 2.25 0 110 2.903l8.153 4.077a2.25 2.25 0 11-.669 1.49v-.027l-8.154-4.078a2.25 2.25 0 11-.001-3.878l8.155-4.078A2.25 2.25 0 0116.5 6z" />
                                </svg>
                                {t('certificateDetail.shareButton')}
                            </a>
                        </div>

                        <div className="bg-white/30 px-4 py-6 dark:bg-white/5 sm:px-10 sm:py-10">
                            <div className="overflow-hidden rounded-2xl border border-white/40 bg-white shadow-inner dark:border-white/10 dark:bg-neutral-900">
                                {certificate.image_url ? (
                                    <img
                                        src={certificate.image_url}
                                        alt={t('certificateDetail.certificateImageAlt')}
                                        className="w-full"
                                    />
                                ) : (
                                    <div className="flex h-96 items-center justify-center text-gray-400">
                                        {t('certificateDetail.certificateUnavailable')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </GlassCard>
                </div>
            </div>
        </AppLayout>
    );
};

const CertificateDetail: React.FC<Props> = (props) => (
    <ThemeWrapper>
        <CertificateDetailContent {...props} />
    </ThemeWrapper>
);

export default CertificateDetail;
